import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import { paths } from "config/paths";
import { useFormik } from "formik";
import Link from "next/link";
import { useWebRouter } from "~utils/next-router/use-web-router";
import { FC, ReactNode } from "react";
import * as Yup from "yup";
import { useGetCurrentAccountCache } from "~auth/queries/useGetCurrentAccount";
import { useGetCurrentUserCache } from "~auth/queries/useGetCurrentUser";
import { useGetCurrentMapAccessMethod } from "~map-view/queries/useGetMapAccessMethod";
import { useGetMapByPasswordMutation } from "~map-view/queries/useGetMapByPasswordMutation";
import { GET_MAP_ACCESS_METHOD_TYPE } from "~map-view/types/__generated/gql/graphql";
import { MangoLogoLoadingBackdrop } from "~ui-components/components/atoms/mango-logo-loading-backdrop";
import { CenteredLayout, MangoLogo, toast } from "~ui-components/index";

export const MapGuard: FC<{ children: ReactNode }> = ({ children }) => {
	const router = useWebRouter();
	const { isLoading: isUserLoading, data: user } = useGetCurrentUserCache();
	const { isLoading: isAccountLoading } = useGetCurrentAccountCache();
	const { isLoading: isMapAccessMethodLoading, data: mapAccessMethod } =
		useGetCurrentMapAccessMethod();

	if (
		isUserLoading ||
		isAccountLoading ||
		isMapAccessMethodLoading ||
		!router.isReady
	)
		return <MangoLogoLoadingBackdrop />;

	if (mapAccessMethod) {
		switch (mapAccessMethod.type) {
			case GET_MAP_ACCESS_METHOD_TYPE.AUTHORIZED_USER:
			case GET_MAP_ACCESS_METHOD_TYPE.HIDDEN_ID:
			case GET_MAP_ACCESS_METHOD_TYPE.PUBLIC:
				return children;
			case GET_MAP_ACCESS_METHOD_TYPE.NOT_FOUND: {
				void router.replace(paths.notFound);
				return <MangoLogoLoadingBackdrop />;
			}
			case GET_MAP_ACCESS_METHOD_TYPE.PASSWORD: {
				if (!sessionStorage.getItem(`map-password:${router._query.mapId}`)) {
					return <MapPasswordInput />;
				}

				return children;
			}
		}
	}

	if (user) {
		void router.replace(paths.unauthorized);
	} else {
		void router.replace(paths.accountName.signIn(router._query.accountName));
	}
	return <MangoLogoLoadingBackdrop />;
};

const MapPasswordInput = () => {
	const router = useWebRouter();
	const getMapByPasswordMutation = useGetMapByPasswordMutation();

	const form = useFormik({
		validateOnMount: true,
		initialValues: {
			password: "",
		},
		validationSchema: Yup.object({
			password: Yup.string().required("Password is required"),
		}),
		onSubmit: (values, helpers) => {
			toast.promise(
				async () => {
					await getMapByPasswordMutation.mutateAsync({
						id: router._query.mapId!,
						password: values.password,
					});
					sessionStorage.setItem(
						`map-password:${router._query.mapId}`,
						values.password,
					);
					router.reload();
				},
				{
					loading: "Verifying password...",
					success: "Password verified successfully!",
					error: (e) => {
						helpers.setSubmitting(false);
						return e.message ?? "Something went wrong!";
					},
				},
			);
		},
	});

	return (
		<CenteredLayout>
			<Stack
				spacing={4}
				component="form"
				onSubmit={form.handleSubmit}>
				<div>
					<Box
						component={Link}
						href={paths.home}
						sx={{ display: "inline-block", fontSize: 0 }}>
						<MangoLogo
							width={122}
							height={32}
						/>
					</Box>
				</div>
				<Card>
					<CardHeader title="Input password for this map" />
					<CardContent>
						<Stack spacing={2}>
							<FormControl>
								<InputLabel>Password</InputLabel>
								<OutlinedInput
									autoFocus
									type="password"
									{...form.getFieldProps("password")}
									error={!!form.touched.password && !!form.errors.password}
								/>
								{!!form.touched.password && !!form.errors.password && (
									<FormHelperText error>{form.errors.password}</FormHelperText>
								)}
							</FormControl>
							<Button
								type="submit"
								variant="contained"
								disabled={form.isSubmitting || !form.isValid}>
								Submit
							</Button>
						</Stack>
					</CardContent>
				</Card>
			</Stack>
		</CenteredLayout>
	);
};
